import { useEffect, useReducer } from 'react';
import { useSocket } from '../../shared/contexts/SocketContext';
import AdminApiKeysPageView from './AdminApiKeysPageView';
import { fetchApiKeys, createApiKey, revokeApiKey, type ApiKeyItem } from './adminApiKeys.api';

interface AdminApiKeysState {
  loading: boolean;
  error: string | null;
  keys: ApiKeyItem[];
  newKeyValue: string | null;
}

type AdminApiKeysAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_KEYS'; payload: ApiKeyItem[] }
  | { type: 'ADD_KEY_CREATED'; payload: { key: ApiKeyItem; rawKey: string } }
  | { type: 'REVOKE_KEY'; payload: string }
  | { type: 'CLEAR_NEW_KEY' }
  | { type: 'RESET_STATE' };

function adminApiKeysReducer(state: AdminApiKeysState, action: AdminApiKeysAction): AdminApiKeysState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_KEYS':
      return { ...state, keys: action.payload, error: null };
    case 'ADD_KEY_CREATED':
      return {
        ...state,
        keys: [action.payload.key, ...state.keys],
        newKeyValue: action.payload.rawKey,
        error: null
      };
    case 'REVOKE_KEY':
      return {
        ...state,
        keys: state.keys.map(k => (k._id === action.payload ? { ...k, isActive: false } : k)),
        error: null
      };
    case 'CLEAR_NEW_KEY':
      return { ...state, newKeyValue: null };
    case 'RESET_STATE':
      return { loading: true, error: null, keys: [], newKeyValue: null };
    default:
      return state;
  }
}

function AdminApiKeysPage() {
  const [state, dispatch] = useReducer(adminApiKeysReducer, {
    loading: true,
    error: null,
    keys: [],
    newKeyValue: null
  });

  const { addNotification } = useSocket();

  async function load() {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const list = await fetchApiKeys();
      dispatch({ type: 'SET_KEYS', payload: list });
    } catch (e: any) {
      dispatch({ type: 'SET_ERROR', payload: e?.message || 'Failed to load API keys' });
      dispatch({ type: 'SET_KEYS', payload: [] });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }

  useEffect(() => { load(); }, []);

  async function onCreate(label: string) {
    try {
      const res = await createApiKey(label);
      const newItem: ApiKeyItem = {
        _id: res._id,
        keyPrefix: res.keyPrefix,
        label: res.label,
        isActive: true,
        createdAt: new Date().toISOString(),
        expiresAt: res.expiresAt || undefined
      };
      dispatch({ type: 'ADD_KEY_CREATED', payload: { key: newItem, rawKey: res.key } });
      addNotification({ type: 'success', message: 'API key created' });
    } catch (e: any) {
      dispatch({ type: 'SET_ERROR', payload: e?.message || 'Failed to create API key' });
      addNotification({ type: 'error', message: e?.message || 'Failed to create API key' });
    }
  }

  async function onRevoke(id: string) {
    try {
      await revokeApiKey(id);
      dispatch({ type: 'REVOKE_KEY', payload: id });
      addNotification({ type: 'success', message: 'API key revoked' });
    } catch (e: any) {
      dispatch({ type: 'SET_ERROR', payload: e?.message || 'Failed to revoke API key' });
      addNotification({ type: 'error', message: e?.message || 'Failed to revoke API key' });
    }
  }

  function onClearNewKey() {
    dispatch({ type: 'CLEAR_NEW_KEY' });
  }

  return (
    <AdminApiKeysPageView
      loading={state.loading}
      error={state.error}
      keys={state.keys}
      newKeyValue={state.newKeyValue}
      onCreate={onCreate}
      onRevoke={onRevoke}
      onClearNewKey={onClearNewKey}
    />
  );
}

export default AdminApiKeysPage;
